import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { AnalyticsTab } from "../components/admin/AnalyticsTab";
import { RegistriesTab, UsersTab } from "../components/admin/Tables";
import { PromotionsTab } from "../components/admin/PromotionsTab";
import { PricingTab } from "../components/admin/PricingTab";
import { CleanupTab } from "../components/admin/CleanupTab";
import { logError } from "../lib/log";

const TABS = ["Analytics", "Registries", "Users", "Promotions", "Pricing", "Cleanup"];

export default function Admin() {
  const [tab, setTab] = useState("Analytics");
  const [data, setData] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [a, r, u, p, i] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/registries"),
        api.get("/admin/users"), api.get("/admin/promos"), api.get("/admin/promos/insights"),
      ]);
      setData({ stats: a.data, registries: r.data, users: u.data, promos: p.data, insights: i.data });
    } catch (error) {
      logError("Admin data load failed", error);
      toast.error("Admin access required");
    }
  }, []);

  const reloadPromos = useCallback(async () => {
    const [{ data: promos }, { data: insights }] = await Promise.all([
      api.get("/admin/promos"), api.get("/admin/promos/insights"),
    ]);
    setData((prev) => (prev ? { ...prev, promos, insights } : prev));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createPromo = async (payload) => {
    await api.post("/admin/promos", payload);
    await reloadPromos();
  };

  const togglePromo = async (code) => {
    await api.patch(`/admin/promos/${code}`);
    await reloadPromos();
  };

  const deletePromo = async (code) => {
    await api.delete(`/admin/promos/${code}`);
    await reloadPromos();
    toast.success("Code removed");
  };

  if (!data) return <div className="py-40 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-300 mx-auto" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-24" data-testid="admin-page">
      <div className="hy-label">Administration</div>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">Registry control</h1>

      <div className="flex flex-wrap gap-2 mt-12 border-b border-amber-500/15">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-6 py-4 text-xs uppercase tracking-widest transition-colors ${tab === t ? "text-amber-200 border-b-2 border-amber-400" : "text-slate-500 hover:text-slate-300"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Analytics" && <AnalyticsTab stats={data.stats} insights={data.insights} />}
      {tab === "Registries" && <RegistriesTab registries={data.registries} />}
      {tab === "Users" && <UsersTab users={data.users} />}
      {tab === "Promotions" && (
        <PromotionsTab promos={data.promos} insights={data.insights} onCreate={createPromo}
          onToggle={togglePromo} onDelete={deletePromo} />
      )}
      {tab === "Pricing" && <PricingTab />}
      {tab === "Cleanup" && <CleanupTab onCleaned={loadAll} />}
    </div>
  );
}
